/* ==========================================================================
   arc + hue — storefront behaviour
   Prototype JS. Cart state is per-page only (no storage) — on Shopify this is
   replaced by /cart.js and the AJAX cart API.
   ========================================================================== */

/* ---------- 1. SVG sprite: petal marks + lamp glyphs ---------------------
   The petal mark is the brand's swappable "+". One symbol per category, so a
   collection recolours and reshapes its mark without new artwork.          */
const ARC_SPRITE = `
<svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;width:0;height:0;overflow:hidden" aria-hidden="true">
  <!-- LIGHTING · 8-point asterisk -->
  <symbol id="p-lighting" viewBox="0 0 100 100">
    <g fill="currentColor"><rect x="44" y="2" width="12" height="96" rx="6"/>
       <rect x="44" y="2" width="12" height="96" rx="6" transform="rotate(45 50 50)"/>
       <rect x="44" y="2" width="12" height="96" rx="6" transform="rotate(90 50 50)"/>
       <rect x="44" y="2" width="12" height="96" rx="6" transform="rotate(135 50 50)"/></g>
  </symbol>
  <!-- TABLEWARE · 12-lobe bloom -->
  <symbol id="p-tableware" viewBox="0 0 100 100">
    <g fill="currentColor"><ellipse cx="50" cy="50" rx="11" ry="48"/>
       <ellipse cx="50" cy="50" rx="11" ry="48" transform="rotate(30 50 50)"/>
       <ellipse cx="50" cy="50" rx="11" ry="48" transform="rotate(60 50 50)"/>
       <ellipse cx="50" cy="50" rx="11" ry="48" transform="rotate(90 50 50)"/>
       <ellipse cx="50" cy="50" rx="11" ry="48" transform="rotate(120 50 50)"/>
       <ellipse cx="50" cy="50" rx="11" ry="48" transform="rotate(150 50 50)"/></g>
  </symbol>
  <!-- DECOR · rounded clover -->
  <symbol id="p-decor" viewBox="0 0 100 100">
    <g fill="currentColor"><circle cx="33" cy="33" r="25"/><circle cx="67" cy="33" r="25"/>
       <circle cx="33" cy="67" r="25"/><circle cx="67" cy="67" r="25"/>
       <rect x="25" y="25" width="50" height="50"/></g>
  </symbol>
  <!-- FURNITURE · soft cross -->
  <symbol id="p-furniture" viewBox="0 0 100 100">
    <path fill="currentColor" d="M38 6h24a8 8 0 0 1 8 8v16a8 8 0 0 0 8 8h16a8 8 0 0 1 8 8v24a8 8 0 0 1-8 8H78a8 8 0 0 0-8 8v16a8 8 0 0 1-8 8H38a8 8 0 0 1-8-8V78a8 8 0 0 0-8-8H6a8 8 0 0 1-8-8V38a8 8 0 0 1 8-8h16a8 8 0 0 0 8-8V14a8 8 0 0 1 8-8z" transform="translate(6 6) scale(.88)"/>
  </symbol>
  <!-- STORAGE · fine starburst -->
  <symbol id="p-storage" viewBox="0 0 100 100">
    <g fill="currentColor"><rect x="47.5" y="0" width="5" height="100"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(22.5 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(45 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(67.5 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(90 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(112.5 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(135 50 50)"/>
       <rect x="47.5" y="0" width="5" height="100" transform="rotate(157.5 50 50)"/>
       <circle cx="50" cy="50" r="14"/></g>
  </symbol>
  <!-- APPLIANCES · 6-point spark -->
  <symbol id="p-appliances" viewBox="0 0 100 100">
    <g fill="currentColor"><ellipse cx="50" cy="50" rx="8" ry="48"/>
       <ellipse cx="50" cy="50" rx="8" ry="48" transform="rotate(60 50 50)"/>
       <ellipse cx="50" cy="50" rx="8" ry="48" transform="rotate(120 50 50)"/></g>
  </symbol>

  <!-- Lamp glyphs (line art, inherit currentColor) -->
  <symbol id="l-selene" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M14 62a46 46 0 0 1 92 0z"/>
      <path d="M44 62v52h32V62"/>
      <path d="M30 118h60"/>
    </g>
  </symbol>
  <symbol id="l-disc" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M12 44a48 22 0 0 1 96 0a48 22 0 0 1-96 0z"/>
      <path d="M60 22v-8M60 66v52"/><path d="M34 122h52"/>
    </g>
  </symbol>
  <symbol id="l-pendant" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M60 4v34"/><path d="M22 76 60 38l38 38z"/><circle cx="60" cy="102" r="20"/>
    </g>
  </symbol>
  <symbol id="l-spiral" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M20 46a40 40 0 0 1 80 0z"/>
      <path d="M60 46c0 14 26 12 26 26s-26 12-26 26 26 12 26 22"/>
      <path d="M40 124h44"/>
    </g>
  </symbol>
  <symbol id="l-column" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M32 20h56l-8 46H40z"/><path d="M60 66v50"/><path d="M36 120h48"/>
    </g>
  </symbol>
  <symbol id="l-globe" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <circle cx="60" cy="56" r="38"/><path d="M46 94v22h28V94"/><path d="M34 120h52"/>
    </g>
  </symbol>
  <symbol id="l-candle" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <path d="M56 8v40"/><path d="M42 48h36l-6 22H48z"/>
      <path d="M60 70v30"/><ellipse cx="60" cy="112" rx="26" ry="10"/>
    </g>
  </symbol>
  <symbol id="l-bulb" viewBox="0 0 120 140">
    <g fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round">
      <circle cx="60" cy="52" r="34"/><path d="M46 86v16h28V86"/><path d="M46 96h28M46 106h28"/>
    </g>
  </symbol>
</svg>`;


/* ==========================================================================
   Theme behaviour. Cart operations use Shopify's AJAX Cart API.
   ========================================================================== */
const AH = window.arcHue || { routes: {}, strings: {}, moneyFormat: '${{amount}}' };

function formatMoney(cents) {
  const value = (cents / 100).toFixed(2);
  const [whole, dec] = value.split('.');
  const spaced = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return (AH.moneyFormat || 'R{{amount}}')
    .replace(/\{\{\s*amount(_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?\s*\}\}/,
      (m, variant) => (variant && variant.includes('no_decimals')) ? spaced : `${spaced}.${dec}`);
}

document.addEventListener('DOMContentLoaded', () => {
  document.body.insertAdjacentHTML('afterbegin', ARC_SPRITE);
  initHeader();
  initAccordions();
  initQuantity();
  initAjaxCart();
  initFacets();
  initQuiz();
  initReveal();
});

/* ---------- Header ---------- */
function initHeader() {
  const burger = document.querySelector('[data-burger]');
  const menu = document.querySelector('[data-mobile-nav]');
  if (!burger || !menu) return;
  burger.addEventListener('click', () => {
    const open = menu.dataset.open === 'true';
    menu.dataset.open = String(!open);
    burger.setAttribute('aria-expanded', String(!open));
  });
}

/* ---------- Accordions ---------- */
function initAccordions() {
  document.querySelectorAll('.acc-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.setAttribute('aria-expanded', String(btn.getAttribute('aria-expanded') !== 'true'));
    });
  });
}

/* ---------- Quantity steppers ---------- */
function initQuantity() {
  document.querySelectorAll('[data-qty]').forEach(wrap => {
    const input = wrap.querySelector('input');
    if (!input) return;
    wrap.querySelector('[data-qty-down]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    wrap.querySelector('[data-qty-up]')?.addEventListener('click', () => {
      input.value = Number(input.value) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

/* ---------- AJAX add to cart ----------
   Any form with [data-ajax-cart] posts to /cart/add.js instead of navigating.
   Falls back to a normal form POST if the request fails. */
function initAjaxCart() {
  document.querySelectorAll('form[data-ajax-cart]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const original = submit ? submit.textContent : '';
      if (submit) { submit.disabled = true; submit.textContent = '…'; }

      const fd = new FormData(form);
      // Bundle forms send items[][id] pairs; build the sections API payload.
      const ids = fd.getAll('items[][id]').filter(Boolean);
      let body;
      if (ids.length) {
        body = JSON.stringify({ items: ids.map(id => ({ id: Number(id), quantity: 1 })) });
      } else {
        body = JSON.stringify({ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) });
      }

      try {
        const res = await fetch(AH.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body
        });
        if (!res.ok) throw new Error((await res.json()).description || 'Add to cart failed');
        await refreshCartCount();
        toast(AH.strings.addedToCart || 'Added to cart');
      } catch (err) {
        toast(err.message);
      } finally {
        if (submit) { submit.disabled = false; submit.textContent = original; }
      }
    });
  });
}

async function refreshCartCount() {
  try {
    const cart = await (await fetch(AH.routes.cart_url + '.js')).json();
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
  } catch (e) { /* count stays as rendered */ }
}

/* ---------- Toast ---------- */
function toast(msg) {
  let t = document.querySelector('.toast');
  if (!t) {
    t = document.createElement('div');
    t.className = 'toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<svg class="petal" aria-hidden="true"><use href="#p-lighting"/></svg><span></span>';
    document.body.appendChild(t);
  }
  t.querySelector('span').textContent = msg;
  t.dataset.show = 'true';
  clearTimeout(t._timer);
  t._timer = setTimeout(() => { t.dataset.show = 'false'; }, 2600);
}

/* ---------- Collection facets ----------
   Submits Shopify's native filter form and swaps the grid without a reload. */
function initFacets() {
  const form = document.querySelector('[data-facets]');
  if (!form) return;

  form.addEventListener('change', () => applyFacets(form));
  form.addEventListener('submit', e => { e.preventDefault(); applyFacets(form); });

  window.addEventListener('popstate', () => { window.location.reload(); });
}

async function applyFacets(form) {
  const params = new URLSearchParams(new FormData(form)).toString();
  const url = `${window.location.pathname}?${params}`;
  form.querySelectorAll('.chip').forEach(chip => {
    const box = chip.querySelector('input[type="checkbox"]');
    if (box) chip.setAttribute('aria-pressed', String(box.checked));
  });
  try {
    const html = await (await fetch(url)).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('#ProductGrid');
    const count = doc.querySelector('[data-result-count]');
    if (fresh) document.querySelector('#ProductGrid').innerHTML = fresh.innerHTML;
    if (count) document.querySelector('[data-result-count]').textContent = count.textContent;
    window.history.pushState({}, '', url);
    initAjaxCart();
    initReveal();
  } catch (e) {
    window.location.href = url;
  }
}

/* ---------- Lamp quiz ---------- */
function initQuiz() {
  const quiz = document.querySelector('[data-quiz]');
  if (!quiz) return;
  const steps = [...quiz.querySelectorAll('.quiz-step')];
  const bars = [...quiz.querySelectorAll('.quiz-progress i')];
  const result = quiz.querySelector('[data-quiz-result]');
  const answers = {};
  let i = 0;

  const show = n => {
    steps.forEach((s, idx) => { s.dataset.active = String(idx === n); });
    bars.forEach((b, idx) => { b.dataset.done = String(idx <= n); });
    i = n;
    if (result) result.hidden = true;
    quiz.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  quiz.addEventListener('click', e => {
    const opt = e.target.closest('.quiz-opt');
    if (opt) {
      answers[opt.dataset.q] = { value: opt.dataset.value, label: opt.dataset.label };
      if (i < steps.length - 1) { show(i + 1); } else { renderResult(); }
      return;
    }
    if (e.target.closest('[data-quiz-back]')) show(Math.max(0, i - 1));
    if (e.target.closest('[data-quiz-restart]')) { Object.keys(answers).forEach(k => delete answers[k]); show(0); }
  });

  function renderResult() {
    if (!result) return;
    steps.forEach(s => { s.dataset.active = 'false'; });

    // Build a native Shopify tag-filtered collection URL from the chosen tags.
    const base = quiz.dataset.collectionUrl || '/collections/all';
    const tags = Object.values(answers).map(a => a.value).filter(Boolean);
    const url = tags.length ? `${base}/${tags.join('+')}` : (result.dataset.fallbackUrl || base);
    const summary = Object.values(answers).map(a => a.label).join(' · ');
    const img = result.dataset.fallbackImage;

    result.innerHTML = `
      <p class="eyebrow eyebrow--rule">${result.dataset.resultHeading || 'Your match'}</p>
      <div class="split" style="align-items:center">
        <div class="tile" style="background:var(--teal-tint);color:var(--teal)">
          ${img ? `<img src="${img}" alt="" style="width:100%;height:100%;object-fit:cover">`
                : '<svg class="lamp" style="width:52%" aria-hidden="true"><use href="#l-selene"/></svg>'}
        </div>
        <div>
          <h2 class="h-2">${result.dataset.fallbackTitle || ''}</h2>
          <p class="lede" style="margin-top:1rem">${result.dataset.resultText || ''}</p>
          <p class="small" style="color:var(--grey-600)">${summary}</p>
          <div class="hero-actions">
            <a class="btn btn--teal" href="${url}">${result.dataset.ctaLabel || 'See your matches'}</a>
            <button class="btn btn--outline" type="button" data-quiz-restart>Start again</button>
          </div>
        </div>
      </div>`;
    result.hidden = false;
    result.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

/* ---------- Scroll reveal ---------- */
function initReveal() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const items = document.querySelectorAll('[data-reveal]:not([data-revealed])');
  if (!items.length || !('IntersectionObserver' in window)) return;
  items.forEach(el => {
    el.dataset.revealed = 'pending';
    el.style.opacity = '0';
    el.style.transform = 'translateY(18px)';
    el.style.transition = 'opacity .7s var(--ease), transform .7s var(--ease)';
  });
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      e.target.style.opacity = '1';
      e.target.style.transform = 'none';
      e.target.dataset.revealed = 'true';
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  items.forEach(el => io.observe(el));
}

window.arcHueHelpers = { toast, refreshCartCount, formatMoney, initAjaxCart };
